// Reading and writing Decisively data objects by path, e.g. "employees/1/roles/2/the role title": segments alternate
// between an entity name and an instance @id, and an odd number of segments ends with an attribute.

const UNSAFE_SEGMENTS = new Set(["__proto__", "prototype", "constructor"]);

const assertSafePath = (path: string, segments: string[]) => {
  for (const segment of segments) {
    if (UNSAFE_SEGMENTS.has(segment)) {
      throw new Error(`Unsafe path segment '${segment}' in path '${path}'`);
    }
  }
};

/** Instance @ids compare as strings, so 1 and "1" are the same instance. */
const sameId = (a: unknown, b: unknown) => a != null && b != null && String(a) === String(b);

/**
 * Gets the value at `path`: an entity instance, or an attribute when the path ends with one. Returns undefined when
 * any part of the path is missing.
 *
 * With `includeParent`, the result (a copy) also has the nearest parent instance, without its entity arrays, under
 * `_<parent entity name>`:
 *
 * getAtPath(obj, "employees/1/roles/1", true) // { "@id": "1", ..., "_employees": { "@id": "1", ... } }
 */
export const getAtPath = (obj: any, path: string, includeParent = false) => {
  if (!obj || !path) return undefined;

  const segments = path.split("/");
  let current = obj;
  let parent: any;
  let parentEntityName: string | undefined;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];

    if (i % 2 === 1) {
      // An instance: find it by @id in the entity array
      if (!Array.isArray(current)) return undefined;
      const foundItem = current.find((item: any) => sameId(item?.["@id"], segment));
      if (!foundItem) return undefined;

      if (includeParent && i < segments.length - 1) {
        parentEntityName = segments[i - 1];
        // A copy without its entity arrays, to avoid circular references
        parent = { ...foundItem };
        for (const key of Object.keys(parent)) {
          if (Array.isArray(parent[key])) delete parent[key];
        }
      }
      current = foundItem;
    } else {
      // An entity name or attribute
      if (typeof current !== "object" || current === null) return undefined;
      current = current[segment];
      if (current === undefined) return undefined;
    }
  }

  if (includeParent && parent && parentEntityName) {
    const result = typeof current === "object" && current !== null ? { ...current } : current;
    if (typeof result === "object" && result !== null) {
      result[`_${parentEntityName}`] = parent;
    }
    return result;
  }

  return current;
};

/**
 * Deep merge of `source` into `target`, in place:
 * - Arrays whose items have @id: merged by @id (unmatched source items are added), recursing into matched items.
 * - Plain objects: merged recursively.
 * - Anything else replaces the target's value.
 * `@parent` and `@path` keys are skipped.
 */
const deepMergeInto = (target: any, source: any): void => {
  if (!source || typeof source !== "object") return;
  for (const key of Object.keys(source)) {
    if (key === "@parent" || key === "@path") continue;
    if (UNSAFE_SEGMENTS.has(key)) continue;
    const srcVal = source[key];
    const tgtVal = target[key];
    if (
      Array.isArray(srcVal) &&
      srcVal.length > 0 &&
      srcVal[0] != null &&
      typeof srcVal[0] === "object" &&
      "@id" in srcVal[0]
    ) {
      if (!Array.isArray(tgtVal)) target[key] = srcVal;
      else {
        for (const srcItem of srcVal) {
          const id = srcItem["@id"];
          if (id == null) continue;
          const item = tgtVal.find((t: any) => t && sameId(t["@id"], id));
          if (!item) tgtVal.push({ ...srcItem });
          else deepMergeInto(item, srcItem);
        }
      }
    } else if (
      srcVal != null &&
      typeof srcVal === "object" &&
      !Array.isArray(srcVal) &&
      tgtVal != null &&
      typeof tgtVal === "object" &&
      !Array.isArray(tgtVal)
    ) {
      deepMergeInto(tgtVal, srcVal);
    } else {
      target[key] = srcVal;
    }
  }
};

export type SetAtPathOptions = {
  /** Create missing entity arrays and instances on the way (default true). When false, nothing is set if any are missing. */
  inferMissing?: boolean;
};

/**
 * Sets `value` at `path` in `obj`, in place:
 * - No path: `value`'s keys are copied onto `obj` (shallow).
 * - "*": `value` is deep merged into `obj` (see deepMergeInto).
 * - A path ending with an instance (even number of segments): `value`'s keys are copied onto that instance, which is
 *   created with `value` if it doesn't exist.
 * - A path ending with an attribute (odd number of segments): the attribute is set to `value`.
 * Missing entity arrays and instances on the way are created, unless `inferMissing` is false.
 */
export const setAtPath = (obj: any, path: string, value: any, options: SetAtPathOptions = {}) => {
  const inferMissing = options.inferMissing !== false;
  if (!obj) return;
  if (!path) {
    if (typeof value === "object" && value !== null) {
      for (const key of Object.keys(value)) {
        if (!UNSAFE_SEGMENTS.has(key)) obj[key] = value[key];
      }
    }
    return;
  }
  if (path === "*") {
    if (typeof value === "object" && value !== null) deepMergeInto(obj, value);
    return;
  }

  const segments = path.split("/");
  assertSafePath(path, segments);
  let current = obj;

  const setAttribute = segments.length % 2 === 1;
  const max = setAttribute ? segments.length - 1 : segments.length;
  for (let i = 0; i < max; i++) {
    const segment = segments[i];

    if (i % 2 === 1) {
      // An instance: find it by @id, or create it
      if (!Array.isArray(current)) {
        if (!inferMissing) return;
        current = [];
      }
      let item = current.find((entry: any) => sameId(entry?.["@id"], segment));
      if (!item) {
        if (!inferMissing) return;
        item = i === max - 1 && !setAttribute ? { "@id": segment, ...value } : { "@id": segment };
        current.push(item);
      } else if (i === max - 1 && !setAttribute) {
        Object.assign(item, value);
      }
      current = item;
    } else {
      // An entity name: get its array, or create it
      if (typeof current !== "object" || current === null) {
        if (!inferMissing) return;
        current = {};
      }
      if (!Array.isArray(current[segment])) {
        if (!inferMissing) return;
        current[segment] = [];
      }
      current = current[segment];
    }
  }
  if (setAttribute) {
    const lastSegment = segments[segments.length - 1];
    if (typeof current === "object" && current !== null) {
      current[lastSegment] = value;
    }
  }
};

/**
 * setAtPath for many attribute writes into one object, with the same result: each instance is found or made once and
 * kept, rather than searched for in its entity's rows on every write. `instance(path)` gives the instance at an
 * instance path ("employees/1/roles/2"), made with any missing on the way, to set attributes on with `attribute`;
 * `set(path, value)` sets one at an attribute path. Only for an object nothing else changes while it's being written.
 */
export const pathWriter = (obj: any) => {
  const instances = new Map<string, any>();
  // each entity array's rows by @id (as strings), built once from what the array already has
  const rowsById = new Map<any[], Map<string, any>>();
  const rowsOf = (rows: any[]) => {
    let byId = rowsById.get(rows);
    if (!byId) {
      byId = new Map();
      for (const row of rows) {
        const id = row?.["@id"];
        // the first row with an @id wins, as setAtPath's find does
        if (id != null && !byId.has(String(id))) byId.set(String(id), row);
      }
      rowsById.set(rows, byId);
    }
    return byId;
  };
  const instance = (path: string): any => {
    let found = instances.get(path);
    if (found) return found;
    const slash = path.lastIndexOf("/");
    if (slash < 0) throw new Error(`Not an instance path: '${path}'`);
    const entitySlash = path.lastIndexOf("/", slash - 1);
    const parent = entitySlash < 0 ? obj : instance(path.slice(0, entitySlash));
    const entity = path.slice(entitySlash + 1, slash);
    const index = path.slice(slash + 1);
    assertSafePath(path, [entity, index]);
    if (!Array.isArray(parent[entity])) parent[entity] = [];
    const byId = rowsOf(parent[entity]);
    found = byId.get(index);
    if (!found) {
      found = { "@id": index };
      parent[entity].push(found);
      byId.set(index, found);
    }
    instances.set(path, found);
    return found;
  };
  const attribute = (target: any, name: string, value: unknown) => {
    if (UNSAFE_SEGMENTS.has(name)) throw new Error(`Unsafe path segment '${name}' in path '${name}'`);
    target[name] = value;
  };
  const set = (path: string, value: unknown) => {
    const slash = path.lastIndexOf("/");
    if (slash < 0) return attribute(obj, path, value);
    const instancePath = path.slice(0, slash);
    if (instancePath.split("/").length % 2 === 1) throw new Error(`pathWriter sets attributes, not instances: '${path}'`);
    attribute(instance(instancePath), path.slice(slash + 1), value);
  };
  return { instance, attribute, set };
};

/**
 * Removes what `path` points at, in place: the instance (even number of segments), taken out of its entity array, or
 * the attribute (odd number of segments). Does nothing when any part of the path is missing.
 */
export const removeAtPath = (obj: any, path: string) => {
  if (!obj || !path) return;
  const segments = path.split("/");
  assertSafePath(path, segments);
  let current = obj;

  // Walk to the parent of what's being removed
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    if (i % 2 === 1) {
      if (!Array.isArray(current)) return;
      current = current.find((item: any) => sameId(item?.["@id"], segment));
      if (!current) return;
    } else {
      if (typeof current !== "object" || current === null) return;
      current = current[segment];
      if (current === undefined) return;
    }
  }

  const lastSegment = segments[segments.length - 1];
  if (segments.length % 2 === 0) {
    if (Array.isArray(current)) {
      const index = current.findIndex((item: any) => sameId(item?.["@id"], lastSegment));
      if (index !== -1) current.splice(index, 1);
    }
  } else if (typeof current === "object" && current !== null) {
    delete current[lastSegment];
  }
};
